import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";

const FALLBACK = [
  {
    image: "https://images.unsplash.com/photo-1695755149872-10a7166b53dd?crop=entropy&cs=srgb&fm=jpg&q=85",
    eyebrow: "Group Tours from Kolkata",
    title: "In-depth journeys, with people who feel like family.",
    body: "Thoughtfully planned group journeys from Kolkata — no rushed checklists, just complete exploration.",
  },
];

export default function HeroSlider() {
  const [slides, setSlides] = useState(FALLBACK);
  const [i, setI] = useState(0);

  useEffect(() => {
    api.get("/hero-slides").then((r) => { if (r.data?.length) { setSlides(r.data); setI(0); } }).catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setInterval(() => setI((v) => (v + 1) % slides.length), 6500);
    return () => clearInterval(t);
  }, [slides.length]);

  const s = slides[i] || slides[0];
  return (
    <section className="relative overflow-hidden bg-slate-900" data-testid="hero-slider">
      <div className="relative h-[78vh] min-h-[560px] max-h-[820px]">
        {slides.map((sl, idx) => (
          <img key={idx} src={fileUrl(sl.image)} alt={sl.eyebrow || sl.title}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${idx === i ? "opacity-100" : "opacity-0"}`} />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/25 to-transparent" />
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 h-full flex items-center">
          <div className="max-w-2xl text-white fade-in" key={i}>
            {s.eyebrow && (
              <span className="inline-block bg-amber-500/25 border border-amber-300/50 text-amber-100 text-xs sm:text-sm font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-6" data-testid="hero-eyebrow">
                {s.eyebrow}
              </span>
            )}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.2] tracking-tight" data-testid="hero-title">
              {s.title}
            </h1>
            {s.body && (
              <p className="mt-6 text-lg sm:text-xl text-amber-50/95 leading-relaxed max-w-xl" data-testid="hero-body">
                {s.body}
              </p>
            )}
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/tours?category=Group%20Tours" className="btn-gold px-7 h-14 rounded-full font-semibold text-lg inline-flex items-center min-w-[200px] justify-center" data-testid="hero-explore-btn">
                Explore Tours
              </Link>
            </div>
          </div>
        </div>
        {slides.length > 1 && (
          <div className="absolute bottom-6 right-4 sm:right-8 flex items-center gap-2">
            <button aria-label="Previous" onClick={() => setI((i - 1 + slides.length) % slides.length)}
              className="w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md text-white grid place-items-center" data-testid="hero-prev">
              <ChevronLeft />
            </button>
            <button aria-label="Next" onClick={() => setI((i + 1) % slides.length)}
              className="w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md text-white grid place-items-center" data-testid="hero-next">
              <ChevronRight />
            </button>
          </div>
        )}
        {slides.length > 1 && (
          <div className="absolute bottom-8 left-4 sm:left-8 flex gap-2">
            {slides.map((_, idx) => (
              <button key={idx} onClick={() => setI(idx)} aria-label={`Slide ${idx + 1}`}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-10 bg-amber-400" : "w-4 bg-white/40"}`} data-testid={`hero-dot-${idx}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
